import React from 'react';
import { TooltipRenderProps } from 'react-joyride';
import { motion } from 'framer-motion';
import { SHARK_SPECIES } from '@/features/admin/components/SharkAvatarSelector';

export const CustomTooltip: React.FC<TooltipRenderProps> = ({
  index,
  step,
  skipProps,
  primaryProps,
  backProps,
  tooltipProps,
  isLastStep,
  size
}) => {
  const adminUserRaw = localStorage.getItem('admin_user');
  const adminUser = adminUserRaw ? JSON.parse(adminUserRaw) : null;
  const sharkSpeciesId = adminUser?.sharkAvatar || 'blue_shark';
  const userCostume = adminUser?.sharkCostume || 'default';
  const currentShark = SHARK_SPECIES.find(s => s.id === sharkSpeciesId) || SHARK_SPECIES[0];
  const currentCostume = currentShark.costumes.find(c => c.id === userCostume) || currentShark.costumes[0];
  const mascotSrc = currentCostume.frontView;

  return (
    <div
      {...tooltipProps}
      style={{
        maxWidth: 'min(360px, calc(100vw - 32px))',
        maxHeight: 'calc(100vh - 32px)',
        width: '100%',
        paddingTop: '20px',
        boxSizing: 'border-box',
        zIndex: 10000
      }}
    >
      {/* 3D Animated Shark floating ABSOLUTELY above top-left of modal */}
      <motion.img
        src={mascotSrc}
        alt={`${currentShark.defaultName} Mascot`}
        initial={{ y: 8, opacity: 0 }}
        animate={{ y: [0, -6, 0], opacity: 1 }}
        transition={{
          y: { repeat: Infinity, duration: 3.2, ease: 'easeInOut' },
          opacity: { duration: 0.3 }
        }}
        style={{
          position: 'absolute',
          top: '-20px',
          left: '-12px',
          width: '72px',
          height: '72px',
          objectFit: 'contain',
          transform: 'scaleX(-1)',
          zIndex: 50,
          pointerEvents: 'none',
          filter: 'drop-shadow(0 10px 14px rgba(0,0,0,0.65))'
        }}
      />

      {/* Inner Scrollable Card */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          backgroundColor: 'var(--admin-bg-container, #010204)',
          backgroundImage: 'radial-gradient(circle 240px at top right, rgba(0, 140, 255, 0.15) 0%, transparent 100%)',
          color: 'var(--admin-card-text, #ffffff)',
          borderRadius: '0',
          padding: '24px 24px 20px 24px',
          maxHeight: 'calc(100vh - 52px)',
          overflowY: 'auto',
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.75), 0 0 0 1px rgba(56, 189, 248, 0.35)',
          border: '1px solid var(--admin-card-border, rgba(255, 255, 255, 0.14))',
          boxSizing: 'border-box'
        }}
      >
        {/* Header Row: Step Badge */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', marginLeft: '48px' }}>
          <span style={{
            fontSize: '10px',
            fontWeight: 700,
            fontFamily: "'Space Grotesk', sans-serif",
            color: '#38bdf8',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            backgroundColor: 'rgba(56, 189, 248, 0.12)',
            padding: '3px 8px',
            borderRadius: '0',
            border: '1px solid rgba(56, 189, 248, 0.3)'
          }}>
            Step {index + 1} of {size}
          </span>
        </div>

        {/* Step Title */}
        {step.title && (
          <h4 style={{
            margin: '0 0 8px 0',
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: '17px',
            fontWeight: 700,
            color: 'var(--admin-card-text, #ffffff)',
            letterSpacing: '-0.01em',
            lineHeight: '1.3'
          }}>
            {step.title}
          </h4>
        )}

        {/* Step Content */}
        <div style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: '13px',
          lineHeight: '1.6',
          color: '#94a3b8',
          marginBottom: '20px'
        }}>
          {step.content}
        </div>

        {/* Footer Controls: Generous spacing between Skip Tour and Back/Next */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '20px',
          paddingTop: '14px',
          borderTop: '1px solid rgba(255, 255, 255, 0.08)'
        }}>
          <button
            {...skipProps}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#64748b',
              fontSize: '12px',
              cursor: 'pointer',
              fontWeight: 600,
              fontFamily: "'Inter', sans-serif",
              padding: '6px 0',
              marginRight: 'auto',
              flexShrink: 0
            }}
            className="hover:text-white transition-colors"
          >
            Skip Tour
          </button>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexShrink: 0 }}>
            {index > 0 && (
              <button
                {...backProps}
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: '0',
                  color: '#e2e8f0',
                  fontSize: '12px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontFamily: "'Inter', sans-serif",
                  padding: '7px 14px'
                }}
                className="hover:bg-white/10 transition-colors"
              >
                Back
              </button>
            )}

            <button
              {...primaryProps}
              style={{
                background: '#38bdf8',
                backgroundImage: 'linear-gradient(to right, #38bdf8, #0ea5e9)',
                color: '#0f172a',
                border: 'none',
                borderRadius: '0',
                padding: '7px 16px',
                fontSize: '12px',
                fontWeight: 700,
                fontFamily: "'Inter', sans-serif",
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(56, 189, 248, 0.3)'
              }}
              className="hover:brightness-110 transition-all"
            >
              {isLastStep ? 'Finish' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
